import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import permissionService from '../../../services/permissionService';
import './AddEditPermission.css';

const AddEditPermission = () => {
  const navigate = useNavigate();
  const { permissionId } = useParams();
  const isEditMode = !!permissionId;

  const [formData, setFormData] = useState({
    permission_name: '',
    route_name: '',
    page_name: ''
  });
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [errors, setErrors] = useState({});

  const fetchPages = useCallback(async () => {
    try {
      const response = await permissionService.getPages();
      if (response.success) {
        setPages(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
    }
  }, []);

  const fetchPermission = useCallback(async () => {
    try {
      setFetching(true);
      const response = await permissionService.getPermission(permissionId);
      
      if (response.success) {
        setFormData({
          permission_name: response.data.name || '',
          route_name: response.data.route_name || '',
          page_name: response.data.page_id || ''
        });
      } else {
        toast.error(response.message || 'Failed to fetch permission');
        navigate('/permission/permissions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch permission';
      toast.error(errorMessage);
      navigate('/permission/permissions');
    } finally {
      setFetching(false);
    }
  }, [permissionId, navigate]);

  useEffect(() => {
    fetchPages();
    if (isEditMode) {
      fetchPermission();
    }
  }, [isEditMode, fetchPages, fetchPermission]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.permission_name || formData.permission_name.trim() === '') {
      newErrors.permission_name = 'Permission name is required';
    }

    if (!formData.route_name || formData.route_name.trim() === '') {
      newErrors.route_name = 'Route name is required';
    }

    if (!formData.page_name) {
      newErrors.page_name = 'Page is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      const data = {
        permission_name: formData.permission_name.trim(),
        route_name: formData.route_name.trim(),
        page_name: formData.page_name
      };

      if (isEditMode) {
        data.permission_id = parseInt(permissionId);
        const response = await permissionService.updatePermission(data);
        
        if (response.success) {
          toast.success('Permission updated successfully');
          navigate('/permission/permissions');
        } else {
          toast.error(response.message || 'Failed to update permission');
        }
      } else {
        const response = await permissionService.createPermission(data);
        
        if (response.success) {
          toast.success('Permission created successfully');
          navigate('/permission/permissions');
        } else {
          toast.error(response.message || 'Failed to create permission');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save permission';
      toast.error(errorMessage);
      
      // Set field-specific errors if available
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/permission/permissions');
  };

  if (fetching) {
    return (
      <div className="add-edit-permission-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading permission...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-edit-permission-page">
      <div className="page-header">
        <h2 className="page-title">
          {isEditMode ? 'Edit Permission Name' : 'Add Permission Name'}
        </h2>
        <button onClick={handleCancel} className="back-btn">
          <i className="fa fa-arrow-left" aria-hidden="true"></i> Back
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="permission-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="permission_name">
                Permission Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="permission_name"
                name="permission_name"
                value={formData.permission_name}
                onChange={handleChange}
                className={errors.permission_name ? 'error' : ''}
                placeholder="Permission Name"
                maxLength={50}
              />
              {errors.permission_name && (
                <span className="error-message">{errors.permission_name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="route_name">
                Route Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="route_name"
                name="route_name"
                value={formData.route_name}
                onChange={handleChange}
                className={errors.route_name ? 'error' : ''}
                placeholder="Route Name"
                maxLength={60}
              />
              {errors.route_name && (
                <span className="error-message">{errors.route_name}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="page_name">
                Page <span className="required">*</span>
              </label>
              <select
                id="page_name"
                name="page_name"
                value={formData.page_name}
                onChange={handleChange}
                className={errors.page_name ? 'error' : ''}
              >
                <option value="">------ Select Page Name ------</option>
                {pages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </select>
              {errors.page_name && (
                <span className="error-message">{errors.page_name}</span>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditPermission;

